import React from 'react';
import { cn } from './utils';

/**
 * Button — Primary (spectrum) / Secondary (outline) / Tertiary (link)
 * Sizes: sm 40h · md 44h · lg 48h
 * Spec: KINDRA_COMPLETE_DESIGN_SYSTEM §4 + KINDRA_DESKTOP_DESIGNS
 */
export const Button = React.forwardRef(({
  variant = 'primary',
  size = 'md',
  as: As = 'button',
  className = '',
  children,
  fullWidth = false,
  ...rest
}, ref) => {
  const base = 'k-press inline-flex items-center justify-center gap-2 font-display font-semibold whitespace-nowrap focus-visible:outline-none transition-all duration-200';
  const sizes = {
    sm: 'h-10 px-4 text-[14px] rounded-pill',
    md: 'h-11 px-5 text-[15px] rounded-pill',
    lg: 'h-12 px-7 text-[16px] rounded-pill',
  };
  const variants = {
    // Primary: spectrum fill on any bg, icy text
    primary:
      'text-icy bg-spectrum shadow-e2 hover:shadow-e3 hover:-translate-y-[1px] active:translate-y-0',
    // Primary on dark (alt) — solid navy fill
    'primary-navy':
      'text-icy bg-navy shadow-e2 hover:shadow-e3 hover:-translate-y-[1px]',
    // Secondary: outline (navy on light, icy on dark) using current ink color
    secondary:
      'border-2 bg-transparent hover:bg-[var(--tint)] dark:hover:bg-[var(--navy-line)]',
    // Tertiary / link
    tertiary:
      'bg-transparent underline-offset-4 hover:underline px-2',
  };
  const secondaryColors = variant === 'secondary'
    ? 'text-[var(--ink)] border-[var(--ink)]'
    : '';
  return (
    <As
      ref={ref}
      className={cn(base, sizes[size], variants[variant], secondaryColors, fullWidth && 'w-full', className)}
      {...rest}
    >
      {children}
    </As>
  );
});
Button.displayName = 'Button';
